// Admin API client — uses admin_token for Authorization. API base URL is set only in api/client.ts.
import { api } from './client';

const API_BASE_URL = api.baseURL;

function getAdminHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  const token = localStorage.getItem('admin_token');
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface MerchantItem {
  id: number;
  name: string;
  email: string;
  merchant_name: string;
  first_name: string;
  last_name: string;
  contact_number: string | null;
  location_address: string | null;
  account_status: string;
  created_at: string;
}

export interface MerchantModule {
  key: string;
  label: string;
  enabled: boolean;
}

export interface MerchantDetail extends MerchantItem {
  modules: MerchantModule[];
}

export interface MerchantDetailResponse {
  data: MerchantDetail;
  available_modules: string[];
}

export interface MerchantsResponse {
  data: MerchantItem[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
}

export const adminApi = {
  baseURL: API_BASE_URL,

  async get<T = unknown>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: getAdminHeaders(),
    });
    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
      throw new Error('Unauthorized');
    }
    return response.json();
  },

  async getMerchantDetail(id: number): Promise<MerchantDetailResponse> {
    return this.get<MerchantDetailResponse>(`/admin/merchants/${id}`);
  },

  async updateMerchant(
    id: number,
    data: { account_status?: string; enabled_modules?: string[] }
  ): Promise<MerchantDetailResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/merchants/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify(data),
    });
    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
      throw new Error('Unauthorized');
    }
    return response.json();
  },

  async getMerchants(params?: { search?: string; page?: number; per_page?: number }): Promise<MerchantsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.per_page) searchParams.set('per_page', String(params.per_page));
    const qs = searchParams.toString();
    const url = `/admin/merchants${qs ? `?${qs}` : ''}`;
    return this.get<MerchantsResponse>(url);
  },

  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        headers: getAdminHeaders(),
      });
    } catch {
      // Ignore errors during logout
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    }
  },
};
